// JSON Schema for recipe_json_steps.
//
// The same schema validates recipes written by people (the manual editor) and
// recipes generated by an LLM through OpenAI structured outputs
// (`text.format: { type: "json_schema", strict: true }`). It therefore
// sticks to the subset strict mode supports: every property is required
// (optional values are nullable instead), `additionalProperties: false`
// everywhere, unions via `anyOf`, and no string-length keywords. Length
// limits are enforced separately (see LIMITS).

import { TAGS } from './tags';
import { UNITS } from './units';

export const COOK_METHODS = [
  'pan',
  'pot',
  'oven',
  'grill',
  'bbq',
  'steamer',
  'deep_fryer',
  'air_fryer',
  'microwave',
  'slow_cooker',
  'pressure_cooker',
  'other',
] as const;

export const HEAT_LEVELS = ['low', 'medium_low', 'medium', 'medium_high', 'high'] as const;

const notes = {
  type: ['string', 'null'],
  description: 'Optional tips for this step as Markdown. null when there are none.',
} as const;

const images = {
  type: 'array',
  items: { type: 'string' },
  description: 'Ids of photos uploaded for this step. [] for a new step; keep the ids as they are when changing an existing one.',
} as const;

const durationMinutes = {
  type: ['number', 'null'],
  exclusiveMinimum: 0,
  description: 'How long this step takes in minutes, shown with a timer button. null when untimed.',
} as const;

export const recipeStepDefs = {
  ingredient_line: {
    type: 'object',
    additionalProperties: false,
    required: ['name', 'quantity', 'unit', 'note'],
    properties: {
      name: {
        type: 'string',
        description: 'Ingredient name without the amount, e.g. "olive oil" or "eggs".',
      },
      quantity: {
        type: ['number', 'null'],
        exclusiveMinimum: 0,
        description: 'Amount in `unit`. null when not measured, e.g. salt "to taste".',
      },
      unit: {
        anyOf: [{ type: 'string', enum: UNITS }, { type: 'null' }],
        description: 'Unit for `quantity`. null for whole items that are counted, e.g. 2 eggs.',
      },
      note: {
        type: ['string', 'null'],
        description: 'Preparation note, e.g. "finely chopped" or "to taste". null when there is none.',
      },
    },
  },
  add_ingredients_step: {
    type: 'object',
    additionalProperties: false,
    description: 'Add one or more ingredients and their amounts.',
    required: ['type', 'instruction', 'ingredients', 'notes', 'images'],
    properties: {
      type: { type: 'string', enum: ['add_ingredients'] },
      instruction: {
        type: ['string', 'null'],
        description: 'Where or how to add them, e.g. "Whisk together in a large bowl". null to simply list them.',
      },
      ingredients: { type: 'array', minItems: 1, items: { $ref: '#/$defs/ingredient_line' } },
      notes,
      images,
    },
  },
  cook_step: {
    type: 'object',
    additionalProperties: false,
    description: 'Apply heat, e.g. "Sauté in a frying pan for 5 minutes" or "Bake at 200°C for 30 minutes".',
    required: ['type', 'instruction', 'method', 'heat', 'temperature_c', 'duration_minutes', 'notes', 'images'],
    properties: {
      type: { type: 'string', enum: ['cook'] },
      instruction: { type: 'string', description: 'What to do, as a full sentence.' },
      method: {
        anyOf: [{ type: 'string', enum: COOK_METHODS }, { type: 'null' }],
        description: 'Main piece of equipment used. null if unclear.',
      },
      heat: {
        anyOf: [{ type: 'string', enum: HEAT_LEVELS }, { type: 'null' }],
        description: 'Hob heat level. null for ovens or when not relevant.',
      },
      temperature_c: {
        type: ['number', 'null'],
        minimum: -50,
        maximum: 500,
        description: 'Temperature in degrees Celsius (oven, oil, etc.). null when not relevant.',
      },
      duration_minutes: durationMinutes,
      notes,
      images,
    },
  },
  prep_step: {
    type: 'object',
    additionalProperties: false,
    description: 'Preparation without heat, e.g. "Dice the tomatoes" or "Line a baking tray with baking paper".',
    required: ['type', 'instruction', 'duration_minutes', 'notes', 'images'],
    properties: {
      type: { type: 'string', enum: ['prep'] },
      instruction: { type: 'string', description: 'What to do, as a full sentence.' },
      duration_minutes: durationMinutes,
      notes,
      images,
    },
  },
} as const;

const stepsProperty = {
  type: 'array',
  minItems: 1,
  description: 'The steps in the order they are carried out.',
  items: {
    anyOf: [
      { $ref: '#/$defs/add_ingredients_step' },
      { $ref: '#/$defs/cook_step' },
      { $ref: '#/$defs/prep_step' },
    ],
  },
} as const;

/** Schema for the `recipe_json_steps` document stored with every recipe. */
export const recipeJsonStepsSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://dishly.app/schemas/recipe_json_steps.json',
  title: 'recipe_json_steps',
  type: 'object',
  additionalProperties: false,
  required: ['steps'],
  properties: { steps: stepsProperty },
  $defs: recipeStepDefs,
} as const;

const draftProperties = {
  name: { type: 'string', description: 'Recipe name.' },
  description: { type: 'string', description: 'Markdown introduction: background, inspiration or tips.' },
  servings: { type: 'integer', minimum: 1 },
  total_time_minutes: { type: 'integer', minimum: 1, description: 'Total time from start to finish in minutes.' },
  tags: { type: 'array', items: { type: 'string', enum: TAGS } },
  steps: stepsProperty,
} as const;

const draftRequired = ['name', 'description', 'servings', 'total_time_minutes', 'tags', 'steps'] as const;

/**
 * A whole recipe written by the LLM from photos or a description (see
 * apps/server/src/ai). The result is saved as a draft and opened in the editor.
 */
export const recipeDraftSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['problem', ...draftRequired],
  properties: {
    problem: {
      type: ['string', 'null'],
      description:
        'null normally. When the input holds no usable recipe or food (an unrelated photo, unreadable text, a request that is not about cooking), a short explanation for the cook; the other fields can then be minimal.',
    },
    ...draftProperties,
  },
  $defs: recipeStepDefs,
} as const;

/** The LLM's answer when a cook asks a question about a recipe, or for a change, while reading it. */
export const recipeAdaptationSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['reply', 'changes', 'recipe'],
  properties: {
    reply: {
      type: 'string',
      description: 'The answer to a question, or one or two friendly sentences about what changed (or why nothing did).',
    },
    changes: {
      type: 'array',
      items: { type: 'string' },
      description:
        'Each change to the dish as a short line, e.g. "Worcestershire sauce → soy sauce with a squeeze of lemon". Not wording edits that follow from it. [] when nothing changed.',
    },
    recipe: {
      anyOf: [
        { type: 'object', additionalProperties: false, required: draftRequired, properties: draftProperties },
        { type: 'null' },
      ],
      description: 'The whole recipe with the change made. null when the recipe stays as it is (questions).',
    },
  },
  $defs: recipeStepDefs,
} as const;

/** Wraps a schema in the shape the OpenAI Responses API expects in `text.format`. */
export function openAITextFormat(name: string, schema: object) {
  const { $schema: _schema, $id: _id, ...rest } = schema as Record<string, unknown>;
  return { type: 'json_schema' as const, name, strict: true, schema: rest };
}
